import React from 'react'
import isEmpty from 'lodash/isEmpty'
import cuid from 'cuid'
import Paper from 'material-ui/Paper'
import {
  Toolbar,
  ToolbarGroup,
  ToolbarTitle,
} from 'material-ui/Toolbar'
import { Divider } from 'semantic-ui-react'
import muiThemeable from 'material-ui/styles/muiThemeable'
import {
  compose,
  withState,
} from 'recompose'
import CmsApis from 'apis/cmsApis'
import withIntl from 'store/intl/withIntl'
import withStoreLocator from 'store/storeLocator/withStoreLocator'
import { withStoreLocatorPreferences } from 'store/preferences/withPreferences'
import withEditPage from 'hocs/extends/withEditPage'
import TypeEditor from 'components/forms/storeLocator/TypeEditor'
import ItemUpdater from 'components/wrappers/ItemUpdater'
import {
  updateItemArrayByIndex,
  removeItemArrayByIndex,
  addItemToArray,
} from 'helpers/arrayHelpers'
import { withModalSubmit } from 'hocs/bases/withModalSubmit'
import Overlay from 'components/modals/Overlay'
import CircleAddButton from 'components/Button/CircleAddButton'
import { withSorter } from 'hocs/bases/withSorter'

const EnhancedTypeEditor = ItemUpdater.Enhance(TypeEditor)

const SortableEnhancedTypeEditor = withSorter()(
  ({
    item,
    saveStatus,
    actionsDisabled,
    order,
    onSave,
    setActionsDisabled,
    onToggleSubmitModal,
    onDelete,
  }) => (
    <div>
      <EnhancedTypeEditor
        value={item.name}
        isSaving={saveStatus.isRequest}
        actionsDisabled={actionsDisabled}
        onSave={(val) => onSave(val, order)}
        onEdit={() => setActionsDisabled(true)}
        onDiscard={() => setActionsDisabled(false)}
        onDelete={() => onToggleSubmitModal(() => onDelete(order))}
      />
      <Divider />
    </div>
  ),
)

const StoreTypes = compose(
  withStoreLocator(),
  withEditPage({
    resourceName: 'storeLocator',
    getFetchAction: () => CmsApis.getStoreLocator,
    getUpdateAction: () => CmsApis.updateStoreLocator,
    getInitialResource: (props) => props.storeLocator,
    getInitialState: (props) => props.storeLocator,
    fetchPredicate: ({ storeLocator }) => !storeLocator || isEmpty(storeLocator),
    getOnFetchSuccess: ({ fetchStoreLocatorSuccess }) => (
      (resource) => fetchStoreLocatorSuccess(resource)
    ),
    getOnUpdateSuccess: ({ updateStoreLocatorSuccess }) => (
      (resource) => updateStoreLocatorSuccess(resource)
    ),
  }),
  withIntl(),
  muiThemeable(),
  withStoreLocatorPreferences(),
  withState('actionsDisabled', 'setActionsDisabled', false),
  withState('isLoading', 'setIsLoading', false),
  withState('newItem', 'setNewItem', null),
  withModalSubmit(),
)(
  ({
    onChange,
    onSave,
    saveStatus,
    storeLocator,
    storeLocator: {
      types: {
        collections = [],
      } = {},
    } = {},
    actionsDisabled,
    setActionsDisabled,
    renderSubmitModal,
    onToggleSubmitModal,
    isLoading,
    setIsLoading,
    newItem,
    setNewItem,
  }) => {
    const handleAddButtonClick = () => {
      setActionsDisabled(true)
      setNewItem({
        id: cuid(),
        name: {
          en: '',
          th: '',
        },
      })
    }
    const saveData = (newData) => {
      const newStoreLocator = {
        ...storeLocator,
        types: { collections: newData },
      }
      return onSave(newStoreLocator)
        .then(() => {
          onChange(newStoreLocator)
          setIsLoading(false)
          return setActionsDisabled(false)
        })
    }
    const handleSortEnd = (newData) => {
      setIsLoading(true)
      return saveData(newData)
    }
    const handleSave = (newValue, index) => {
      const newData = updateItemArrayByIndex(collections, index, (item) => ({
        ...item,
        name: newValue,
      }))
      return saveData(newData)
    }
    const handleDelete = (index) => {
      const newData = removeItemArrayByIndex(collections, index)
      setIsLoading(true)
      return saveData(newData)
        .then(() => setIsLoading(false))
    }
    return (
      <Paper
        style={{
          position: 'relative',
          paddingBottom: 30,
        }}
      >
        <Toolbar>
          <ToolbarGroup>
            <ToolbarTitle text={'Name'} />
          </ToolbarGroup>
        </Toolbar>
        <Overlay.Loading visible={isLoading} />
        <SortableEnhancedTypeEditor
          collection={collections}
          onUpdate={(val) => onChange({
            ...storeLocator,
            types: { collections: val },
          })}
          isSaving={saveStatus.isRequest}
          actionsDisabled={actionsDisabled}
          saveStatus={saveStatus}
          onSave={handleSave}
          setActionsDisabled={setActionsDisabled}
          onToggleSubmitModal={onToggleSubmitModal}
          onDelete={handleDelete}
          onRawChange={handleSortEnd}
        />
        {newItem && (
          <ItemUpdater
            showActions
            onSave={() => {
              setIsLoading(true)
              return saveData(addItemToArray(collections, newItem))
                .then(() => {
                  setActionsDisabled(false)
                  setIsLoading(false)
                  setNewItem(null)
                })
            }}
            onDiscard={() => {
              setActionsDisabled(false)
              setNewItem(null)
            }}
          >
            <TypeEditor
              isEditing
              value={newItem.name}
              onChange={(val) => setNewItem({
                ...newItem,
                name: val,
              })}
            />
          </ItemUpdater>
        )}
        {(!newItem && !actionsDisabled) && (
          <CircleAddButton
            centered
            onClick={handleAddButtonClick}
          />
        )}
        {renderSubmitModal()}
      </Paper>
    )
  },
)

StoreTypes.propTypes = {}
StoreTypes.defaultProps = {}

export default StoreTypes



// WEBPACK FOOTER //
// ./src/pages/StoreLocator/StoreTypes.js